import { Button, Modal as BaseModal } from 'antd';
import styled from 'styled-components';

import { OBSERVATION_EDIT_CARD_WIDTH } from 'horizon/components/Damages/ObservationEdit/ObservationEdit.style';
import { getSeverityColor } from 'utils/color/helpers';

export const Modal = styled(BaseModal)<{ $showingBanner?: boolean }>`
  left: 0.5rem;
  height: ${({ $showingBanner }) =>
    $showingBanner ? 'calc(100vh - 1rem - 34px)' : 'calc(100vh - 1rem)'};
  margin: 0;
  max-width: unset;
  padding: 0;
  top: ${({ $showingBanner }) => ($showingBanner ? 'calc(0.5rem + 34px)' : '0.5rem')};
  width: calc(100vw - 1rem) !important;

  div:has(> .ant-modal-content),
  .ant-modal-content {
    height: 100%;
    width: 100%;
  }

  .ant-modal-body {
    display: flex;
    height: 100%;
    padding: 0;
    position: relative;
    width: 100%;
  }

  .ant-modal-close {
    border-radius: 2px;
    border: 1px solid ${({ theme }) => theme.buttonBorderColor};
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${({ theme }) => theme.buttonBackgroundColor};
    &:hover {
      background-color: ${({ theme }) => theme.secondaryBackgroundColor};
    }
  }

  .ant-modal-close-x {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    width: auto;
    height: auto;

    color: ${({ theme }) => theme.headerColor};
    &:hover {
      color: ${({ theme }) => theme.tealActive};
    }
  }
`;

export const Pane = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;

  &:nth-of-type(2) {
    border-left: 1px solid ${({ theme }) => theme.borderColor};
  }
`;

export const PaneOverlay = styled.div<{ $isEditing?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: ${({ $isEditing }) =>
      $isEditing ? `minmax(${OBSERVATION_EDIT_CARD_WIDTH}, 1fr)` : '1fr'} 40px auto 40px 1fr;
  pointer-events: none;
  padding: 8px;

  > * {
    pointer-events: auto;
  }
`;

export const PaneTopRightActions = styled.div`
  position: absolute;
  top: 12px;
  right: 20px;
  z-index: 6;
  display: flex;
  gap: 8px;
  height: auto !important;
`;

export const ImageContainer = styled.div`
  height: 100%;
  position: relative;
`;

/**
 * The observation will be drawn in the color corresponding to its severity if 4-5,
 * teal if severity < 4 or unset
 */
type ObservationPolygonProps = {
  $coordinates?: [number, number][] | null;
  $selected: boolean;
  $severity: number | null | undefined;
};

export const ObservationPolygon = styled.polygon.attrs<ObservationPolygonProps>(
  ({ $coordinates }) => ({
    points: $coordinates?.map(xy => xy.join(',')).join(' '),
  })
)<ObservationPolygonProps>`
  fill: none;
  stroke: ${({ theme, $severity }) => getSeverityColor(theme, $severity) ?? theme.teal};
  vector-effect: non-scaling-stroke;
  @supports (vector-effect: non-scaling-stroke) {
    stroke-width: ${({ $selected }) => ($selected ? '3px' : '1px')};
  }
  pointer-events: visible;

  &:hover {
    stroke-width: 6px;
    cursor: pointer;
  }
`;

export const ViewportIndicatorPolygon = styled.polygon.attrs<{
  $coordinates?: [number, number][];
}>(({ $coordinates }) => ({
  points: $coordinates?.map(xy => xy.join(',')).join(' '),
}))<{
  $coordinates?: [number, number][];
}>`
  fill: none;
  pointer-events: none;
  stroke: ${({ theme }) => theme.imageViewportIndicatorColor};
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
`;

export const ViewportIndicatorCasingPolygon = styled(ViewportIndicatorPolygon)`
  stroke: ${({ theme }) => theme.imageViewportIndicatorCasingColor};
  stroke-width: 5px;
`;

export const InferenceBoundaryPolygon = styled.polygon.attrs<{
  $coordinates?: [number, number][] | null;
}>(({ $coordinates }) => ({
  points: $coordinates?.map(xy => xy.join(',')).join(' '),
}))<{
  $coordinates?: [number, number][] | null;
}>`
  fill: none;
  stroke: ${({ theme }) => theme.lightBlue};
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  pointer-events: visible;

  &:hover {
    stroke-width: 3px;
    cursor: pointer;
  }
`;

export const DebugInferenceBoundaryPolygon = styled.polygon.attrs<{
  $coordinates?: [number, number][] | null;
}>(({ $coordinates }) => ({
  points: $coordinates?.map(xy => xy.join(',')).join(' '),
}))<{
  $coordinates?: [number, number][] | null;
}>`
  fill: none;
  stroke: ${({ theme }) => theme.teal};
  stroke-dasharray: 4 2;
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  pointer-events: visible;

  &:hover {
    stroke-width: 3px;
    cursor: default;
  }
`;

export const StyledButton = styled(Button)`
  padding: 4px 8px;
`;

export const SplitViewLinkButton = styled(StyledButton)`
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 8;
`;

export const ThumbnailHeader = styled.div<{ $hasControlsOnly?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ $hasControlsOnly }) => ($hasControlsOnly ? 'center' : 'space-between')};
  padding: 0 0.5rem;
  position: absolute;
  row-gap: 4px;
  top: 0.5rem;
  width: 100%;
  z-index: 1;
`;

export const PanoramaContainer = styled.div`
  /* Remove focus outline for SVG children */
  svg:focus,
  svg *:focus {
    outline: none;
  }

  /* Apply cursor to container and canvas on hover */
  &.crosshair-cursor,
  &.crosshair-cursor canvas {
    cursor: crosshair !important;
  }

  &.no-grab-cursor,
  &.no-grab-cursor canvas {
    cursor: default !important;
  }

  /* Polygon hover inside SVG */
  > svg > polygon:hover {
    cursor: crosshair !important;
  }
`;

export const HotkeysList = styled.div`
  display: grid;
  gap: 8px;
  min-width: 220px;
  padding-top: 20px;
`;

export const HotkeyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  padding-bottom: 8px;
`;

export const HotkeyIconRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const HotkeyKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.mapControlsBorderColor};
  background: ${({ theme }) => theme.mapControlsButtonBackground};
  color: ${({ theme }) => theme.mapControlsTextColor};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  box-shadow: inset 0 -1px 0 ${({ theme }) => theme.mapControlsShadowMedium};
`;

export const EmptyPictureContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
